<template>
  <div class="cookies-policy-page">
    <DeliveryHero
      :title="heading"
      subtitle="The technologies we use, why we use them, and how you can control them."
    />
    <div class="main-layout">
      <DeliverySidebar
        ref="sidebar"
        :sections="sections"
        :active-id="activeId"
        @navigate="scrollToSection"
      />
      <div class="policy-content">
        <p class="policy-intro reveal">
          This Cookie Notice applies to hooliganseyewear.com and was last
          updated {{ lastUpdated }}. It should be read with our
          <a href="/privacy_policy">Privacy Policy</a>. We use “cookies” below
          as a short term for cookies, pixels, local storage, and similar
          browser technologies.
        </p>

        <PolicySection
          class="reveal"
          section-id="overview"
          eyebrow="Overview"
          title="How Our Choices Work"
        >
          <p>
            Essential technologies operate because they are needed to secure the
            store, remember your cart, process checkout, and save your privacy
            choice. For our United States storefront, Analytics and Advertising
            operate by default. You can turn either category off at any time
            through “Your privacy choices” in the footer. Rejecting optional
            technologies does not prevent you from shopping or checking out.
          </p>
          <CalloutBox>
            <strong>Your choice lasts for up to 180 days</strong> in browser
            storage, and the first-party vf_privacy cookie keeps it for up to
            400 days, renewed when you visit, unless you change it sooner, clear
            site data, use another browser or device, or we materially change
            this notice.
          </CalloutBox>
          <button
            class="privacy-choice-button"
            type="button"
            @click="openChoices"
          >
            Open your privacy choices
          </button>
        </PolicySection>

        <PolicySection
          class="reveal"
          section-id="essential"
          eyebrow="Always Active"
          title="Essential Technologies"
        >
          <p>
            These support requested store functions and are not used for
            cross-context behavioral advertising. Blocking them in your browser
            may break the cart, account, fraud prevention, or payment flow.
          </p>
          <div
            class="cookie-inventory"
            role="region"
            aria-label="Essential technology inventory"
            tabindex="0"
          >
            <table>
              <thead>
                <tr>
                  <th>Provider / identifier</th>
                  <th>Purpose</th>
                  <th>Typical duration</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    Hooligans Eyewear / OpenCart session (including OCSESSID)
                  </td>
                  <td>Cart, account session, checkout, and security</td>
                  <td>Session or the period configured by the store</td>
                </tr>
                <tr>
                  <td>Hooligans Eyewear / vf_cookie_consent</td>
                  <td>
                    Saves category choices, policy version, date, expiry, and
                    GPC status in local storage
                  </td>
                  <td>Up to 180 days</td>
                </tr>
                <tr>
                  <td>Hooligans Eyewear / vf_privacy (cookie)</td>
                  <td>
                    Stores your privacy choice (Analytics and Advertising on or
                    off) and the date it was set, so an opt-out is kept even if
                    browser storage is cleared
                  </td>
                  <td>Up to 400 days; renewed when you visit</td>
                </tr>
                <tr>
                  <td>Stripe / Stripe.js and payment security identifiers</td>
                  <td>Payment processing, fraud prevention, and security</td>
                  <td>Session or as described by Stripe</td>
                </tr>
              </tbody>
            </table>
          </div>
        </PolicySection>

        <PolicySection
          class="reveal"
          section-id="analytics"
          eyebrow="Optional"
          title="Analytics Technologies"
        >
          <p>
            When Analytics is enabled, we use analytics technologies so we can
            understand store usage, page performance, product interactions,
            carts, and completed purchases. This includes Google Analytics and
            Microsoft Clarity, which Google Tag Manager runs as approved tags,
            and PostHog, which the store loads directly. Clarity provides
            session recordings, heatmaps, and aggregate interaction insights;
            sensitive page content is masked by default. We send Clarity the
            names of ecommerce funnel events, such as product views,
            add-to-cart, checkout steps, and purchases, but do not intentionally
            send names, email addresses, payment details, prescription
            information, or order numbers.
          </p>
          <p>
            PostHog is our product analytics service. It receives the pages you
            view (with sensitive web-address parameters removed) and product,
            cart, and checkout events, but never payment details or order
            numbers. If you sign in, it also receives your customer identifier
            and account email address; if you check out without an account, it
            receives the email address you gave for the order, as described in
            our <a href="/privacy_policy">Privacy Policy</a>. PostHog session
            recording is not used.
          </p>
          <div
            class="cookie-inventory"
            role="region"
            aria-label="Analytics technology inventory"
            tabindex="0"
          >
            <table>
              <thead>
                <tr>
                  <th>Provider / identifier</th>
                  <th>Purpose</th>
                  <th>Typical duration</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Google Analytics / _ga, _ga_&lt;container-id&gt;</td>
                  <td>Store usage, acquisition, and ecommerce measurement</td>
                  <td>Up to 2 years; browser limits may shorten it</td>
                </tr>
                <tr>
                  <td>Microsoft Clarity / _clck, _clsk</td>
                  <td>
                    Session recordings, heatmaps, interaction diagnostics, and
                    ecommerce funnel-event analysis
                  </td>
                  <td>
                    _clck up to 1 year; _clsk generally groups activity for a
                    short session window
                  </td>
                </tr>
                <tr>
                  <td>PostHog / ph_&lt;project-token&gt;_posthog (cookie)</td>
                  <td>
                    Pseudonymous visitor and session ID for product analytics
                  </td>
                  <td>Up to 180 days; browser limits may shorten it</td>
                </tr>
                <tr>
                  <td>
                    PostHog / ph_&lt;project-token&gt;_posthog_cpm (cookie)
                  </td>
                  <td>
                    PostHog's list of the analytics properties it keeps in its
                    cookie; set for this site only
                  </td>
                  <td>Up to 180 days; browser limits may shorten it</td>
                </tr>
                <tr>
                  <td>PostHog / ph_* (local storage)</td>
                  <td>
                    Analytics state, such as visitor, session, and settings
                  </td>
                  <td>Until removed or Analytics is turned off</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>
            We do not intentionally send prescription details, payment card
            data, or other consumer health information to analytics providers.
          </p>
        </PolicySection>

        <PolicySection
          class="reveal"
          section-id="advertising"
          eyebrow="Optional"
          title="Advertising and Marketing Technologies"
        >
          <p>
            When Advertising is enabled, we may load Meta Pixel, Google
            advertising tags, and Klaviyo onsite tracking. They help measure
            campaigns, personalize ads or messages, understand product and cart
            activity, and operate marketing sign-up forms.
          </p>
          <div
            class="cookie-inventory"
            role="region"
            aria-label="Advertising technology inventory"
            tabindex="0"
          >
            <table>
              <thead>
                <tr>
                  <th>Provider / identifier</th>
                  <th>Purpose</th>
                  <th>Typical duration</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Meta Pixel / _fbp, _fbc</td>
                  <td>Advertising delivery, attribution, and measurement</td>
                  <td>Up to 90 days</td>
                </tr>
                <tr>
                  <td>
                    Google advertising tags / _gcl_* and related identifiers
                  </td>
                  <td>
                    Advertising conversion measurement and campaign attribution
                  </td>
                  <td>Provider-configured; commonly up to 90 days</td>
                </tr>
                <tr>
                  <td>Klaviyo / __kla_id and browser storage</td>
                  <td>
                    Marketing forms, identification after a marketing
                    interaction, and ecommerce messaging
                  </td>
                  <td>Up to 2 years; browser limits may shorten it</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>
            We do not sell personal information for money. Some disclosures to
            advertising providers can nevertheless be treated as “sale,”
            “sharing,” or “targeted advertising” under state privacy laws. You
            can opt out using the controls on this page.
          </p>
        </PolicySection>

        <PolicySection
          class="reveal"
          section-id="signals"
          eyebrow="Browser Signals"
          title="Global Privacy Control"
        >
          <p>
            We recognize an enabled Global Privacy Control (GPC) signal in your
            browser as an opt-out of advertising, sale, and sharing on that
            browser or device. GPC overrides an older Advertising choice. GPC
            also turns off PostHog product analytics on that browser. You may
            still choose whether to allow other Analytics. We currently do not
            treat the legacy Do Not Track signal as a consent choice because
            browsers and websites do not apply it consistently.
          </p>
        </PolicySection>

        <PolicySection
          class="reveal"
          section-id="withdrawal"
          eyebrow="Control"
          title="Change or Withdraw Your Choice"
        >
          <p>
            Use “Your privacy choices” in the footer or the button above at any
            time. When you withdraw a previously granted category, we stop
            future events, attempt to remove related first-party identifiers,
            and reload the page so category-gated third-party code is no longer
            active. Google Tag Manager remains available to receive the denied
            Consent Mode state, but denied tags do not receive permission to
            run. You may clear site data in browser settings for a complete
            local reset.
          </p>
          <p>
            Choices are stored only in this browser: in local storage and in the
            first-party vf_privacy cookie, which keeps your choice for up to 400
            days and is renewed when you visit. Our server sets that cookie
            again so browser storage limits do not erase an opt-out. We do not
            create a server profile or collect an IP address solely to remember
            this choice.
          </p>
        </PolicySection>

        <PolicySection
          class="reveal"
          section-id="contact"
          eyebrow="Questions"
          title="Contact and Updates"
        >
          <p>
            Vendors and identifiers can change when store features change. We
            review this inventory and will request a new choice when a material
            change requires it. For privacy questions or rights requests,
            contact
            <a href="mailto:support@hooliganseyewear.com"
              >support@hooliganseyewear.com</a
            >.
          </p>
        </PolicySection>

        <ContactCta
          class="reveal"
          heading="Questions about your privacy choices?"
          description="Contact us and we will help explain the technologies used by the store."
          button-text="Contact us"
          href="mailto:support@hooliganseyewear.com"
        />
      </div>
    </div>
  </div>
</template>

<script>
import "./cookies_policy.css";
import DeliveryHero from "~/components/delivery/DeliveryHero.vue";
import DeliverySidebar from "~/components/delivery/DeliverySidebar.vue";
import PolicySection from "~/components/delivery/PolicySection.vue";
import ContactCta from "~/components/delivery/ContactCta.vue";
import CalloutBox from "~/components/ui/CalloutBox.vue";
import { useStaticPageSeo } from "~/core/composables/useStaticPageSeo";

// The one place for the notice's revision date; release prep sets it.
const COOKIE_NOTICE_LAST_UPDATED = "October 6, 2026";

export default {
  name: "CookiesPolicyPage",
  components: {
    DeliveryHero,
    DeliverySidebar,
    PolicySection,
    ContactCta,
    CalloutBox,
  },
  setup() {
    return useStaticPageSeo({
      title: "Cookie Notice & Privacy Choices | Hooligans Eyewear",
      heading: "Cookie Notice & Privacy Choices",
      description:
        "Review essential, analytics, and advertising technologies used by Hooligans Eyewear and manage your privacy choices.",
    });
  },
  data() {
    return {
      lastUpdated: COOKIE_NOTICE_LAST_UPDATED,
      activeId: "overview",
      sections: [
        { id: "overview", long: "How Choices Work" },
        { id: "essential", long: "Essential" },
        { id: "analytics", long: "Analytics" },
        { id: "advertising", long: "Advertising" },
        { id: "signals", long: "Global Privacy Control" },
        { id: "withdrawal", long: "Change Your Choice" },
        { id: "contact", long: "Contact and Updates" },
      ],
    };
  },
  mounted() {
    if (typeof window === "undefined") return;
    this._reduceMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this._onScroll = () => this._updateActive();
    window.addEventListener("scroll", this._onScroll, { passive: true });
    window.addEventListener("resize", this._onScroll, { passive: true });
    this.$nextTick(() => this._updateActive());
    this._setupRevealObserver();
  },
  beforeUnmount() {
    if (typeof window === "undefined") return;
    window.removeEventListener("scroll", this._onScroll);
    window.removeEventListener("resize", this._onScroll);
    this._revealObserver?.disconnect();
  },
  methods: {
    openChoices() {
      window.dispatchEvent(new CustomEvent("vf:open-cookie-settings"));
    },
    _setupRevealObserver() {
      const elements = this.$el.querySelectorAll(".reveal");
      if (this._reduceMotion || typeof IntersectionObserver === "undefined") {
        elements.forEach((element) => element.classList.add("visible"));
        return;
      }
      this._revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add("visible");
            this._revealObserver.unobserve(entry.target);
          });
        },
        { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
      );
      elements.forEach((element) => this._revealObserver.observe(element));
    },
    _getClickOffset() {
      return window.innerWidth <= 900 ? 80 : 24;
    },
    _updateActive() {
      const elements = this.sections
        .map((section) => ({
          id: section.id,
          element: document.getElementById(section.id),
        }))
        .filter((item) => item.element);
      if (!elements.length) return;
      let current = elements[0].id;
      elements.forEach(({ id, element }) => {
        if (element.getBoundingClientRect().top <= 120) current = id;
      });
      this.activeId = current;
    },
    scrollToSection(id) {
      const target = document.getElementById(id);
      if (!target) return;
      const top =
        target.getBoundingClientRect().top +
        window.scrollY -
        this._getClickOffset();
      window.scrollTo({ top, behavior: "smooth" });
      this.activeId = id;
    },
  },
};
</script>
